/**
 * Reprodução dos sons da bandeja.
 *
 * O som não viaja pela chamada: o servidor avisa quem está na sala e cada
 * cliente busca o arquivo e toca sozinho. Meio megabyte por clique multiplicado
 * por todo mundo seria caro para entregar algo que o navegador guarda em cache
 * já na segunda vez, e passando pela chamada o áudio ainda chegaria comprimido
 * para voz, que é o pior formato possível para música.
 */

/**
 * Quantos podem soar ao mesmo tempo.
 *
 * Uma sala animada consegue disparar vários no mesmo segundo, e sem teto o
 * resultado é ruído em cima de ruído. O mais antigo cede a vez.
 */
const SIMULTANEOS = 4

const tocando = new Set<HTMLAudioElement>()

/** Guarda os elementos já criados para o segundo clique começar na hora. */
const preparados = new Map<string, HTMLAudioElement>()

export interface OpcoesDeToque {
  /** De 0 a 200, como os outros volumes do aplicativo. */
  volume: number
  /** Saída escolhida nas preferências, quando o navegador deixa escolher. */
  outputDeviceId?: string
}

/**
 * Deixa o arquivo pronto antes do primeiro clique.
 *
 * Sem isso o som só sai depois de baixar, e o atraso entre apertar e ouvir é
 * justamente o que faz uma bandeja parecer quebrada. É chamado quando o cursor
 * passa pelo botão, e não ao abrir a bandeja: baixar duas dezenas de arquivos
 * de uma vez custaria alguns megabytes para tocar um só.
 */
export function prepararSom(url: string): void {
  if (preparados.has(url)) return

  const audio = new Audio()
  audio.preload = 'auto'
  audio.src = url
  preparados.set(url, audio)
}

export function esquecerSom(url: string): void {
  preparados.delete(url)
}

export async function tocarSom(url: string, opcoes: OpcoesDeToque): Promise<void> {
  if (opcoes.volume <= 0) return

  if (tocando.size >= SIMULTANEOS) {
    const maisAntigo = tocando.values().next().value
    if (maisAntigo) {
      maisAntigo.pause()
      tocando.delete(maisAntigo)
    }
  }

  // Uma cópia por toque: o mesmo elemento tocado duas vezes seguidas reinicia
  // em vez de sobrepor, e apertar o botão duas vezes rápido é justamente o que
  // as pessoas fazem.
  const base = preparados.get(url)
  const audio = base ? (base.cloneNode() as HTMLAudioElement) : new Audio(url)

  // O volume do elemento vai até 1; acima disso não há como subir sem passar
  // por um ganho da Web Audio, e para um efeito curto não compensa a
  // complexidade.
  audio.volume = Math.min(1, Math.max(0, opcoes.volume / 100))

  const comSaida = audio as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> }
  if (opcoes.outputDeviceId && typeof comSaida.setSinkId === 'function') {
    // Firefox e Safari não têm; ali o som sai pela saída padrão do sistema.
    await comSaida.setSinkId(opcoes.outputDeviceId).catch(() => {})
  }

  tocando.add(audio)
  const encerrar = () => tocando.delete(audio)
  audio.addEventListener('ended', encerrar, { once: true })
  audio.addEventListener('error', encerrar, { once: true })

  try {
    await audio.play()
  } catch {
    // Navegador que ainda não recebeu um clique bloqueia a reprodução. Não há o
    // que fazer além de deixar passar: o próximo som, depois de qualquer
    // interação, funciona.
    encerrar()
  }
}

/** Cala tudo de uma vez, para quando a pessoa para de ouvir a sala ou sai. */
export function silenciarBandeja(): void {
  for (const audio of tocando) audio.pause()
  tocando.clear()
}
